// HR: payroll, team, time and hiring, with HR's side menu.
import { createRoute, Link, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Icon, type IconName } from "../../components/Icon";
import { Hiring } from "../../screens/Field";
import { Payroll, TimeSheet } from "../../screens/Payroll";
import { FeatureSettings } from "../../screens/settings/Feature";
import { TeamSettings } from "../../screens/settings/More";
import { Team } from "../../screens/Team";
import { Allowed, app, page } from "./base";

/** HR's side menu, like Marketing's (2026-10-30: the row of tabs on top read poorly). Payroll first, it is opened most (2026-11-02). */
const HR_MENU: [string, string, IconName][] = [
  ["/hr/payroll", "Payroll", "money"],
  ["/hr/team", "Team", "team"],
  ["/hr/time", "Time", "clock"],
  ["/hr/hiring", "Hiring", "person"],
];

function HrTabs({ children }: { children: ReactNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  return (
    <div className="settings-layout section-layout">
      <aside className="settings-side section-side">
        <nav className="subnav" aria-label="HR">
          <div className="subnav-group">
            <span className="subnav-title">HR</span>
            {HR_MENU.map(([to, label, icon]) => (
              <Link key={to} to={to} data-status={path === to ? "active" : undefined}>
                <Icon name={icon} size={16} />
                {label}
              </Link>
            ))}
          </div>
        </nav>
      </aside>
      <div className="settings-main">{children}</div>
    </div>
  );
}

const team = createRoute({
  getParentRoute: () => app,
  path: "/hr/team",
  validateSearch: (s: Record<string, unknown>): { invite?: boolean } =>
    s.invite === true || s.invite === "true" ? { invite: true } : {},
  component: () => (
    <Allowed action="people.read">
      <HrTabs>
        <Team />
      </HrTabs>
    </Allowed>
  ),
});

export const hrRoutes = [
  team,
  page("/hr/payroll", () => (
    <Allowed action="payroll.read">
      <HrTabs>
        <Payroll />
      </HrTabs>
    </Allowed>
  )),
  page("/hr/time", () => (
    <Allowed action="payroll.read">
      <HrTabs>
        <TimeSheet />
      </HrTabs>
    </Allowed>
  )),
  page("/hr/hiring", () => (
    <Allowed action="people.read">
      <HrTabs>
        <Hiring />
      </HrTabs>
    </Allowed>
  )),
  page("/hr/settings", () => (
    <Allowed action="settings.read">
      <FeatureSettings title="HR settings" back="/hr/payroll" backLabel="Payroll">
        <TeamSettings />
      </FeatureSettings>
    </Allowed>
  )),
] as const;
